import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/*
 * TopServicesList — DESIGN_SYSTEM.md §2.4
 *
 * Ordered list with rank badge, name, mini-bar (`svc-bar`), count.
 * Rank colors:
 *   1 → brand-500
 *   2 → brand-700
 *   3 → brand-300
 *   4–5 → line-strong (muted)
 */

export interface TopServiceItem {
  id: string | number;
  name: ReactNode;
  /** Numeric count rendered on the right. */
  count: number;
}

interface TopServicesListProps {
  items: TopServiceItem[];
  /** Hide bars (just rank + name + count). Default false. */
  hideBars?: boolean;
  className?: string;
}

function rankClass(rank: number): string {
  if (rank === 1) return 'bg-brand-500 text-white';
  if (rank === 2) return 'bg-brand-700 text-white';
  if (rank === 3) return 'bg-brand-300 text-brand-700';
  return 'bg-surface-2 text-soft';
}

function barClass(rank: number): string {
  if (rank === 1) return 'bg-brand-500';
  if (rank === 2) return 'bg-brand-700';
  if (rank === 3) return 'bg-brand-300';
  return 'bg-line-strong';
}

export function TopServicesList({ items, hideBars, className }: TopServicesListProps) {
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <ol className={cn('flex flex-col gap-2', className)}>
      {items.map((item, i) => {
        const rank = i + 1;
        const pct = (item.count / max) * 100;
        return (
          <li
            key={item.id}
            className="flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-surface-2/60"
          >
            <span
              className={cn(
                'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-pill text-body-xs font-semibold tabular-nums',
                rankClass(rank),
              )}
              aria-label={`Rank ${rank}`}
            >
              {rank}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-body-sm font-medium text-ink">{item.name}</p>
              {!hideBars && (
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-pill bg-surface-2">
                  <div
                    className={cn('h-full', barClass(rank))}
                    style={{ width: `${pct}%` }}
                    aria-hidden
                  />
                </div>
              )}
            </div>
            <span className="text-body-sm font-semibold tabular-nums text-ink">{item.count}</span>
          </li>
        );
      })}
    </ol>
  );
}
